import { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import { getAllAuditLogs } from "../../api/services";
import {
  Search, Download, RefreshCw,
  Info, AlertTriangle, AlertCircle,
  ChevronLeft, ChevronRight, CheckCircle2,
  Mail, Calendar
} from "lucide-react";

// Define severity levels based on action types
const getSeverity = (action = "") => {
  if (action.includes("DELETE") || action.includes("DISABLE")) return "critical";
  if (action.includes("RESET") || action.includes("EXPORT")) return "warning";
  if (action.includes("CREATE") || action.includes("ENABLE")) return "info";
  return "info";
};

const SEV = {
  info:     { icon: Info,          color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe", label: "Info"     },
  warning:  { icon: AlertTriangle, color: "#d97706", bg: "#fffbeb", border: "#fde68a", label: "Warning"  },
  critical: { icon: AlertCircle,   color: "#dc2626", bg: "#fef2f2", border: "#fca5a5", label: "Critical" },
};

function actionColor(action = "") {
  if (action.includes("DELETE") || action.includes("DISABLE")) return "#dc2626";
  if (action.includes("RESET")  || action.includes("EXPORT"))  return "#d97706";
  if (action.includes("CREATE") || action.includes("ENABLE"))  return "#16a34a";
  if (action.includes("UPDATE") || action.includes("EDIT"))    return "#3b82f6";
  return "#6b7280";
}

const AV_COLORS = [
  ["#eef3fd","#1b4fd8"], ["#f0fdf4","#16a34a"], ["#eff6ff","#3b82f6"],
  ["#fff7ed","#ea580c"], ["#fdf4ff","#a21caf"], ["#f1f5f9","#475569"],
];
function avColor(name) { 
  return AV_COLORS[(name?.charCodeAt(0) ?? 0) % AV_COLORS.length]; 
}

const fmtDT = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  return date.toLocaleString("en-GB", { 
    day: "numeric", 
    month: "short", 
    year: "numeric",
    hour: "2-digit", 
    minute: "2-digit" 
  });
};

function Skeleton({ className = "" }) {
  return <div className={`animate-pulse bg-neutral-100 rounded-lg ${className}`} />;
}

const UI_PAGE = 8;

export default function AdminLogs() {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState("");
  const [uiPage,  setUiPage]  = useState(0);
  const [toasts,  setToasts]  = useState([]);

  const showToast = (msg, type = "success") => {
    const id = Date.now();
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3500);
  };

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllAuditLogs({ page: 0, size: 100 });
      // Handle both array and paginated response
      const logsData = response.content || (Array.isArray(response) ? response : []);
      setLogs(logsData);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLogs(); }, []);

  const filtered = logs.filter(log => {
    const matchQ = !search || 
      `${log.action ?? ""} ${log.performedByEmail ?? ""} ${log.targetUserEmail ?? ""} ${log.details ?? ""}`.toLowerCase()
        .includes(search.toLowerCase());
    return matchQ;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / UI_PAGE));
  const paged      = filtered.slice(uiPage * UI_PAGE, (uiPage + 1) * UI_PAGE);

  useEffect(() => setUiPage(0), [search]);

  if (error) return (
    <AdminLayout title="Audit Logs" subtitle="System event trail">
      <div className="flex flex-col items-center justify-center gap-3 py-20">
        <AlertCircle size={32} className="text-red-400" strokeWidth={1.5} />
        <p className="text-[14px] font-semibold text-neutral-700">Failed to load audit logs</p>
        <p className="text-[12px] text-neutral-400">{error}</p>
        <button onClick={loadLogs}
          className="flex items-center gap-1.5 mt-2 px-4 h-9 rounded-lg text-[13px] font-semibold border border-neutral-200 hover:bg-neutral-50">
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="Audit Logs" subtitle={`${logs.length} events`}>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={13} className="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 text-neutral-300" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search events, users, emails..."
            className="w-full h-9 pl-8 pr-3 border border-neutral-200 rounded-lg bg-white text-[13px] outline-none focus:border-[#1b4fd8]" />
        </div>
        <button onClick={() => showToast("CSV export started", "success")}
          className="flex items-center gap-1.5 px-3.5 h-9 rounded-lg text-[13px] font-semibold border border-neutral-200 text-neutral-700 bg-white hover:bg-neutral-50">
          <Download size={13} /> Export CSV
        </button>
        <button onClick={() => { loadLogs(); showToast("Logs refreshed", "success"); }}
          className="flex items-center justify-center bg-white border rounded-lg w-9 h-9 text-neutral-500 border-neutral-200 hover:bg-neutral-50">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Table */}
      <div className="overflow-hidden bg-white border border-neutral-100 rounded-xl" style={{ boxShadow: "0 1px 3px rgba(17,24,39,.08)" }}>

        <div className="hidden md:grid items-center gap-3 px-5 py-2.5 bg-neutral-50 border-b border-neutral-100"
          style={{ gridTemplateColumns: "28px 1fr 200px 200px 100px" }}>
          {["","Event","Performed By","Target User","Timestamp","Severity"].map(h => (
            <div key={h} className="text-[11px] font-semibold text-neutral-400 uppercase tracking-[.07em]">{h}</div>
          ))}
        </div>

        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="grid items-start gap-3 px-5 py-4 border-b border-neutral-100"
                style={{ gridTemplateColumns: "28px 1fr 200px 200px 100px" }}>
                <Skeleton className="w-4 h-4 mt-0.5" />
                <div className="flex flex-col gap-1.5">
                  <Skeleton className="w-32 h-3" /><Skeleton className="w-full h-3" /><Skeleton className="h-2.5 w-24" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="w-5 h-5 rounded-full" /><Skeleton className="w-20 h-3" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="w-5 h-5 rounded-full" /><Skeleton className="w-20 h-3" />
                </div>
                <Skeleton className="h-3 w-28" />
                <Skeleton className="w-16 h-5 rounded-full" />
              </div>
            ))
          : paged.length === 0
          ? <div className="py-14 text-center text-[13px] text-neutral-400">No logs match your filters.</div>
          : paged.map((log, i) => {
              const severity = getSeverity(log.action);
              const sev      = SEV[severity];
              const SevIcon  = sev.icon;
              
              // Performed by user details
              const performedByName = log.performedByEmail?.split('@')[0] || "System";
              const performedByEmail = log.performedByEmail || "system@izsoftwares.com";
              const [performedBg, performedFg] = avColor(performedByName);
              const performedInitials = performedByName.split('.').map(n => n[0]).join('').slice(0, 2).toUpperCase();
              
              // Target user details
              const targetName = log.targetUserEmail?.split('@')[0] || "—";
              const targetEmail = log.targetUserEmail || "—";
              const [targetBg, targetFg] = avColor(targetName);
              const targetInitials = targetName.split('.').map(n => n[0]).join('').slice(0, 2).toUpperCase();
              
              // Format action for better display
              const formattedAction = log.action?.replace(/_/g, ' ') || "Unknown Action";
              
              return (
                <div key={log.id ?? i}
                  className="grid items-start gap-3 px-5 py-4 transition-colors border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60"
                  style={{ gridTemplateColumns: "28px 1fr 200px 200px 100px" }}>
                  <div className="mt-0.5"><SevIcon size={13} strokeWidth={2} style={{ color: sev.color }} /></div>
                  <div>
                    <div className="text-[12px] font-bold tracking-[.02em]" style={{ color: actionColor(log.action) }}>
                      {formattedAction}
                    </div>
                    <div className="text-[12px] text-neutral-500 mt-1">{log.details || "—"}</div>
                    {log.targetUserId && (
                      <div className="text-[11px] text-neutral-300 mt-1">Target ID: {log.targetUserId}</div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0"
                        style={{ background: performedBg, color: performedFg }}>{performedInitials}</div>
                      <span className="text-[12px] text-neutral-700 truncate">{performedByName}</span>
                    </div>
                    <div className="flex items-center gap-1 ml-7">
                      <Mail size={10} className="text-neutral-300" />
                      <span className="text-[10px] text-neutral-400 truncate">{performedByEmail}</span>
                    </div>
                  </div>
                  <div>
                    {targetName !== "—" ? (
                      <>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0"
                            style={{ background: targetBg, color: targetFg }}>{targetInitials}</div>
                          <span className="text-[12px] text-neutral-700 truncate">{targetName}</span>
                        </div>
                        <div className="flex items-center gap-1 ml-7">
                          <Mail size={10} className="text-neutral-300" />
                          <span className="text-[10px] text-neutral-400 truncate">{targetEmail}</span>
                        </div>
                      </>
                    ) : (
                      <span className="text-[12px] text-neutral-400 ml-7">—</span>
                    )}
                  </div>
                  <div className="text-[11px] text-neutral-400">
                    <div className="flex items-center gap-1">
                      <Calendar size={10} className="flex-shrink-0" />
                      <span>{fmtDT(log.performedAt)}</span>
                    </div>
                  </div>
                  <div>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-0.5"
                      style={{ background: sev.bg, color: sev.color, border: `1px solid ${sev.border}` }}>
                      {sev.label}
                    </span>
                  </div>
                </div>
              );
            })
        }

        <div className="flex items-center justify-between px-5 py-3 border-t border-neutral-100">
          <span className="text-[12px] text-neutral-400">{loading ? "Loading…" : `${filtered.length} events`}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setUiPage(p => Math.max(0, p - 1))} disabled={uiPage === 0 || loading}
              className="flex items-center justify-center rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100 disabled:opacity-30">
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
              <button key={i} onClick={() => setUiPage(i)}
                className="w-7 h-7 rounded-lg text-[12px] font-medium"
                style={uiPage === i ? { background: "#1b4fd8", color: "#fff" } : { color: "#737373" }}>
                {i + 1}
              </button>
            ))}
            <button onClick={() => setUiPage(p => Math.min(totalPages - 1, p + 1))} disabled={uiPage >= totalPages - 1 || loading}
              className="flex items-center justify-center rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100 disabled:opacity-30">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Toast */}
      <div className="fixed bottom-6 right-6 z-[999] flex flex-col gap-2">
        {toasts.map(t => (
          <div key={t.id} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-medium min-w-[240px]"
            style={{ background: t.type === "success" ? "#f0fdf4" : "#fef2f2", border: `1px solid ${t.type === "success" ? "#bbf7d0" : "#fca5a5"}`, color: t.type === "success" ? "#16a34a" : "#dc2626", boxShadow: "0 4px 16px rgba(17,24,39,.1)" }}>
            <CheckCircle2 size={14} strokeWidth={2} className="flex-shrink-0" />{t.msg}
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}